import type { QueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Platform } from 'react-native'
import Purchases, { LOG_LEVEL } from 'react-native-purchases'

import type { RevenueCatInitState } from './revenuecat-init-controller'
import type { User } from './useAuth0Adapter'
import { customerInfoQueryOptions } from '../queries/account-queries'
import { queryKeys } from '../queries/query-keys'
import { verifyBackendEntitlement } from '../services/entitlementService'
import { addBreadcrumb, captureException } from '../utils/sentry'
import {
  buildRevenueCatAttemptKey,
  getRevenueCatApiKey,
  installRevenueCatWebLogHandler,
} from './revenuecat-config'
import {
  createRevenueCatInitController,
  revenueCatInitIdleState,
} from './revenuecat-init-controller'
import { normalizeRevenueCatId } from './revenuecat-ownership'
import {
  syncRevenueCatMixpanelDistinctId,
  syncRevenueCatProfile,
} from './revenuecat-profile-sync'
import { useRevenueCatPurchase } from './useRevenueCatPurchase'

type CustomerInfo = Awaited<ReturnType<typeof Purchases.getCustomerInfo>>

interface UseRevenueCatOptions {
  user: User | undefined | null
  revenueCatId: string | null
  mixpanelId: string | null
  shouldInitialize: boolean
  queryClient: QueryClient
  getAccessToken: () => Promise<string | null>
}

export function useRevenueCat({
  user,
  revenueCatId,
  mixpanelId,
  shouldInitialize,
  queryClient,
  getAccessToken,
}: UseRevenueCatOptions) {
  const [revenueCatInitState, setRevenueCatInitState] =
    useState<RevenueCatInitState>(revenueCatInitIdleState)
  const retrySequenceRef = useRef(0)
  const prevRevenueCatUserIdRef = useRef<string | null>(null)
  const prevRevenueCatEmailRef = useRef<string | null>(null)
  const prevRevenueCatNameRef = useRef<string | null>(null)
  const revenueCatLogoutInFlightRef = useRef(false)
  const isPurchasesConfiguredRef = useRef(false)
  const authUserId = user?.sub ?? null
  const controllerRef = useRef(
    createRevenueCatInitController(setRevenueCatInitState),
  )

  const setCustomerInfo = useCallback(
    (info: CustomerInfo | undefined) => {
      queryClient.setQueryData(
        customerInfoQueryOptions(authUserId).queryKey,
        info,
      )
    },
    [authUserId, queryClient],
  )

  const clearCustomerInfo = useCallback(() => {
    queryClient.removeQueries({
      queryKey: customerInfoQueryOptions(authUserId).queryKey,
      exact: true,
    })
  }, [authUserId, queryClient])

  const userId =
    revenueCatId != null && revenueCatId.trim().length > 0
      ? revenueCatId.trim()
      : null
  const userEmail = user?.email ?? null
  const userName = user?.name ?? null

  const resetRevenueCatIdentity = useCallback(() => {
    prevRevenueCatUserIdRef.current = null
    prevRevenueCatEmailRef.current = null
    prevRevenueCatNameRef.current = null
  }, [])

  const getPurchasesConfiguredState = useCallback(async () => {
    const configured = await Purchases.isConfigured?.()
    return configured ?? isPurchasesConfiguredRef.current
  }, [])

  const safeRevenueCatLogout = useCallback(async () => {
    if (revenueCatLogoutInFlightRef.current) {
      return
    }

    try {
      revenueCatLogoutInFlightRef.current = true
      // This app uses custom App User IDs only. Avoid SDK logOut() because
      // RevenueCat creates a new anonymous App User ID on logout, which does
      // not match our identity model. Docs:
      // https://www.revenuecat.com/docs/customers/identifying-customers
      prevRevenueCatEmailRef.current = null
      prevRevenueCatNameRef.current = null
      prevRevenueCatUserIdRef.current = null
    } catch (error) {
      console.warn('RevenueCat logOut skipped', error)
      addBreadcrumb('purchase', 'revenuecat_logout_skipped', {}, 'warning')
    } finally {
      revenueCatLogoutInFlightRef.current = false
    }
  }, [])

  const doSyncProfile = useCallback(
    () =>
      syncRevenueCatProfile({
        userId,
        userEmail,
        userName,
        prevUserIdRef: prevRevenueCatUserIdRef,
        prevEmailRef: prevRevenueCatEmailRef,
        prevNameRef: prevRevenueCatNameRef,
      }),
    [userId, userEmail, userName],
  )

  const doSyncMixpanel = useCallback(
    () =>
      syncRevenueCatMixpanelDistinctId({
        userId,
        mixpanelId,
        authUserId,
      }),
    [userId, mixpanelId, authUserId],
  )

  const resetRevenueCatState = useCallback(async () => {
    controllerRef.current.setIdle()
    resetRevenueCatIdentity()
    clearCustomerInfo()
    await safeRevenueCatLogout()
    isPurchasesConfiguredRef.current = false
  }, [clearCustomerInfo, resetRevenueCatIdentity, safeRevenueCatLogout])

  const initializeRevenueCat = useCallback(
    async ({
      attemptKey,
      manualRetry,
    }: {
      attemptKey: string
      manualRetry: boolean
    }) => {
      const initContext = {
        platform: Platform.OS,
        authUserIdPresent: authUserId != null,
        revenueCatIdPresent: userId != null,
        mixpanelIdPresent: mixpanelId != null,
        manualRetry,
      }

      return controllerRef.current.run(attemptKey, async (lifecycle) => {
        lifecycle.setInitializing()
        addBreadcrumb('purchase', 'revenuecat_init_started', initContext)

        const apiKey = getRevenueCatApiKey()
        if (!apiKey) {
          const error = new Error('RevenueCat API Key not found')
          lifecycle.fail(error)
          if (lifecycle.isCurrent()) {
            isPurchasesConfiguredRef.current = false
            addBreadcrumb(
              'purchase',
              'revenuecat_init_failed',
              { ...initContext, stage: 'configure', message: error.message },
              'warning',
            )
            captureException(error, {
              feature: 'purchase',
              tags: {
                flow: 'revenuecat_init',
                platform: Platform.OS,
                manualRetry: String(manualRetry),
              },
              extra: { ...initContext, stage: 'configure' },
            })
          }
          return
        }

        if (userId == null) {
          const error = new Error(
            'RevenueCat ID missing after backend user became ready',
          )
          lifecycle.fail(error)
          if (lifecycle.isCurrent()) {
            isPurchasesConfiguredRef.current = false
            addBreadcrumb(
              'purchase',
              'revenuecat_init_failed',
              { ...initContext, stage: 'configure', message: error.message },
              'warning',
            )
            captureException(error, {
              feature: 'purchase',
              tags: {
                flow: 'revenuecat_init',
                platform: Platform.OS,
                manualRetry: String(manualRetry),
              },
              extra: { ...initContext, stage: 'configure' },
            })
          }
          return
        }

        try {
          Purchases.setLogLevel(LOG_LEVEL.DEBUG)
          installRevenueCatWebLogHandler()
          const wasConfigured = await getPurchasesConfiguredState()
          if (!wasConfigured) {
            Purchases.configure({ apiKey, appUserID: userId })
            addBreadcrumb(
              'purchase',
              'revenuecat_configure_succeeded',
              initContext,
            )
          } else {
            addBreadcrumb(
              'purchase',
              'revenuecat_configure_skipped_already_configured',
              initContext,
            )
          }
          isPurchasesConfiguredRef.current = true
          lifecycle.setConfigured()

          let activeCustomerInfo = await Purchases.getCustomerInfo()
          if (lifecycle.isCurrent()) {
            setCustomerInfo(activeCustomerInfo)
          }
          if (!lifecycle.isCurrent()) {
            return
          }

          const currentAppUserId = normalizeRevenueCatId(
            activeCustomerInfo.originalAppUserId,
          )
          const shouldSwitchUser = wasConfigured && currentAppUserId !== userId

          if (shouldSwitchUser) {
            lifecycle.setIdentifying()
            addBreadcrumb(
              'purchase',
              'revenuecat_identify_started',
              initContext,
            )

            const { customerInfo } = await Purchases.logIn(userId)
            if (!lifecycle.isCurrent()) {
              return
            }
            activeCustomerInfo = customerInfo
          }

          prevRevenueCatUserIdRef.current = userId
          prevRevenueCatEmailRef.current = null
          prevRevenueCatNameRef.current = null

          await doSyncMixpanel()
          await doSyncProfile()
          await verifyBackendEntitlement(
            activeCustomerInfo,
            authUserId,
            userId,
            getAccessToken,
          )

          if (!lifecycle.isCurrent()) {
            return
          }

          setCustomerInfo(activeCustomerInfo)
          if (shouldSwitchUser) {
            addBreadcrumb(
              'purchase',
              'revenuecat_identify_succeeded',
              initContext,
            )
          }
          lifecycle.setReady()
        } catch (error) {
          const failureStage =
            lifecycle.getState().status === 'identifying'
              ? 'identify'
              : 'configure'
          const normalizedError = lifecycle.fail(error)

          if (!lifecycle.isCurrent()) {
            return
          }

          isPurchasesConfiguredRef.current = lifecycle.getState().isConfigured
          console.error('Failed to initialize RevenueCat', error)
          addBreadcrumb(
            'purchase',
            'revenuecat_init_failed',
            {
              ...initContext,
              stage: failureStage,
              message: normalizedError.message,
            },
            'warning',
          )
          captureException(error, {
            feature: 'purchase',
            tags: {
              flow: 'revenuecat_init',
              platform: Platform.OS,
              manualRetry: String(manualRetry),
            },
            extra: { ...initContext, stage: failureStage },
          })
        }
      })
    },
    [
      authUserId,
      doSyncMixpanel,
      doSyncProfile,
      getAccessToken,
      getPurchasesConfiguredState,
      mixpanelId,
      setCustomerInfo,
      userId,
    ],
  )

  useEffect(() => {
    if (!shouldInitialize) {
      void resetRevenueCatState()
      return
    }

    const attemptKey = buildRevenueCatAttemptKey(authUserId, userId, 'auto')
    void initializeRevenueCat({ attemptKey, manualRetry: false })
  }, [
    authUserId,
    initializeRevenueCat,
    resetRevenueCatState,
    shouldInitialize,
    userId,
  ])

  useEffect(() => {
    if (
      revenueCatInitState.status !== 'ready' ||
      !isPurchasesConfiguredRef.current
    ) {
      return
    }

    const listener = (customerInfo: CustomerInfo) => {
      setCustomerInfo(customerInfo)
    }

    Purchases.addCustomerInfoUpdateListener(listener)
    return () => {
      Purchases.removeCustomerInfoUpdateListener(listener)
    }
  }, [revenueCatInitState.status, setCustomerInfo])

  useEffect(() => {
    if (
      revenueCatInitState.status !== 'ready' ||
      !isPurchasesConfiguredRef.current ||
      !userId ||
      prevRevenueCatUserIdRef.current !== userId
    ) {
      return
    }

    void doSyncProfile()
  }, [revenueCatInitState.status, doSyncProfile, userId])

  const { purchase } = useRevenueCatPurchase({
    userId,
    userEmail,
    userName,
    authUserId,
    revenueCatInitStatus: revenueCatInitState.status,
    isPurchasesConfiguredRef,
    setCustomerInfo,
    getAccessToken,
  })

  const refreshUserStatus = useCallback(async () => {
    console.info('Forcing user status refresh...')
    try {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.user.profile(authUserId),
      })

      if (!isPurchasesConfiguredRef.current) {
        return
      }

      const customerInfo = await Purchases.getCustomerInfo()
      await verifyBackendEntitlement(
        customerInfo,
        authUserId,
        userId,
        getAccessToken,
      )
      setCustomerInfo(customerInfo)
    } catch (error) {
      console.error('Failed to refresh user status', error)
      captureException(error, {
        feature: 'purchase',
        tags: { flow: 'refresh_status' },
      })
    }
  }, [authUserId, getAccessToken, queryClient, setCustomerInfo, userId])

  const retryInitialization = useCallback(async () => {
    if (!shouldInitialize) {
      return
    }

    retrySequenceRef.current += 1
    resetRevenueCatIdentity()
    clearCustomerInfo()
    isPurchasesConfiguredRef.current = false

    const attemptKey = buildRevenueCatAttemptKey(
      authUserId,
      userId,
      `retry-${retrySequenceRef.current}`,
    )

    await initializeRevenueCat({
      attemptKey,
      manualRetry: true,
    })
  }, [
    authUserId,
    clearCustomerInfo,
    initializeRevenueCat,
    resetRevenueCatIdentity,
    shouldInitialize,
    userId,
  ])

  return {
    revenueCatInitStatus: revenueCatInitState.status,
    revenueCatInitError: revenueCatInitState.error,
    isRevenueCatReady: revenueCatInitState.status === 'ready',
    isRevenueCatConfigured: revenueCatInitState.isConfigured,
    purchase,
    refreshUserStatus,
    retryInitialization,
    safeRevenueCatLogout,
  }
}
